<script setup lang="ts">
const { defineOgMeta } = useSeoAttributes()

const technologies = useTechnologies()
const projects = useProjects()

const route = useRoute()
const lp = useLocalePath()
const {
  public: { baseUrl },
} = useRuntimeConfig()

const slug = route.params.slug as string

const { pending, data: tech } = await useAsyncData(
  `technologies_${slug}`,
  async () => {
    return technologies.fetchBySlug(route.params.slug as string)
  },
  { watch: [route] },
)

if (!tech) {
  throw showError({
    statusCode: 404,
  })
}

const { data: list } = await useAsyncData('projects', async () => projects.fetch())

const filteredProjects = computed(() => {
  if (!list.value) {
    return
  }

  return list.value.projects.filter((project) => {
    return project.technologies.some((tech) => tech.slug === slug)
  })
})

if (tech) {
  useHead(() => ({
    title: tech.value?.technology.name,
    meta: defineOgMeta({
      title: tech.value?.technology.title as string,
      description: tech.value?.technology.tech_description as string,
      image: tech.value?.technology.image,
    }),
    link: [{ rel: 'canonical', href: `${baseUrl}/tech/${route.params.slug}` }],
  }))
}
</script>

<template>
  <Loader :pending="pending">
    <main>
      <Container class="screen-content relative grid grid-cols-4 flex-col px-2 py-32 text-center lg:gap-8 lg:px-0">
        <div class="col-span-full mb-8 flex items-start pl-6 md:pl-12 lg:col-span-1 lg:mb-0 lg:mt-3 lg:pl-16">
          <NuxtImg
            :src="tech?.technology.image.url"
            :alt="tech?.technology.title"
            :width="256"
            :height="256"
            fit="cover"
            class="aspect-square w-32 rounded-lg lg:mx-auto"
          />
        </div>

        <header
          id="header"
          class="col-span-4 flex flex-col items-start justify-end gap-y-2 px-6 text-left md:px-12 lg:col-span-3 lg:pl-0 lg:pr-16"
        >
          <Heading as="h1" size="large" class="mx-auto w-full max-w-2xl md:mx-0">
            {{ tech!.technology.title }}
          </Heading>
        </header>

        <aside
          class="col-span-full flex w-full flex-col rounded-lg p-6 md:px-12 lg:col-span-1 lg:pl-16 lg:pr-0"
        ></aside>

        <article class="col-span-4 lg:col-span-3">
          <main class="w-full px-6 pb-16 text-left md:px-12 lg:pl-0 lg:pr-16">
            <RichText :raw="String(tech?.technology.tech_description)" />
          </main>
        </article>
      </Container>

      <section>
        <Container class="flex flex-col items-center">
          <Heading as="h1" size="big" class="mx-auto mb-8 w-full max-w-2xl text-center md:mx-0 md:mb-16">
            {{ $t('tech.projects') }} {{ ' ' }}{{ tech?.technology.name }}
          </Heading>
          <div class="grid-col-1 mx-auto grid max-w-screen-xl gap-4 space-x-2 px-16 lg:grid-cols-3 xl:px-0">
            <ProjectRowPreview
              v-for="project of filteredProjects"
              :key="project.slug"
              :project="project"
              :industry="project.industriesSlugs"
            />
          </div>
        </Container>
      </section>

      <LazyCta :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
    </main>
  </Loader>
</template>
